import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarPlus, FilePlus2 } from 'lucide-react';
import { IITH, SERIF, SANS, heroBackground } from '@/pages/landing/iithBrand';
import { IITH_LOGO_URL } from '@/config/iithAssets';

/** Full-bleed patient home hero — brand-first campus welcome. */
export default function PatientHomeHero({ name, branding = {} }) {
  const primary = branding.primary_color || IITH.saffron;
  const logo = branding.logo_url || IITH_LOGO_URL;
  const hospital = branding.tagline ? null : IITH.hospital;
  const style = heroBackground(primary, null);

  return (
    <section
      className="relative -mx-4 sm:-mx-6 mb-8 overflow-hidden rounded-3xl min-h-[42vh] flex items-end"
      style={style}
      data-testid="patient-home-hero"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />
      <div
        className="absolute top-0 inset-x-0 h-1"
        style={{ background: `linear-gradient(90deg, ${primary}, ${IITH.gold}, ${primary})` }}
      />
      <div className="relative w-full p-6 sm:p-10">
        <div className="flex flex-wrap items-end gap-5 justify-between">
          <div className="max-w-xl">
            <img
              src={logo}
              alt=""
              className="h-14 w-14 sm:h-16 sm:w-16 object-contain bg-white rounded-full p-1.5 shadow-lg mb-4"
            />
            <p
              className="text-[10px] sm:text-xs uppercase tracking-[0.28em] mb-2"
              style={{ color: IITH.gold, fontFamily: SANS }}
            >
              {IITH.institute}
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight"
              style={{ fontFamily: SERIF }}
            >
              Welcome{name ? `, ${name.split(' ')[0]}` : ''}
            </h1>
            <p className="mt-3 text-sm sm:text-base text-[#9AADBE] max-w-md leading-relaxed">
              Your campus health hub — book OPD, track visits, request referrals, and care for your family in one place.
            </p>
          </div>
          <div className="flex flex-wrap gap-2.5">
            <Link
              to="/patient/appointments/book"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-[#071B34] bg-white shadow-lg"
            >
              <CalendarPlus className="h-4 w-4" /> Book appointment
            </Link>
            <Link
              to="/patient/referrals/new"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white border border-white/30"
              style={{ background: primary }}
            >
              <FilePlus2 className="h-4 w-4" /> Request referral
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        {hospital ? (
          <p className="mt-6 text-[11px] text-white/50 tracking-wide">{IITH.hospital} · {IITH.campus}</p>
        ) : null}
      </div>
    </section>
  );
}
